"use client";
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { CSSProperties } from 'react';
import useSWR from 'swr';
import { getApiUrl } from '@/src/config/api';
import Image from 'next/image';
import { FaSearch, FaTimes, FaChevronLeft, FaChevronRight, FaTimesCircle } from 'react-icons/fa';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
const API = '/api';
const fetcher = async (url: string) => {
  // If the URL already includes the full path, use it as is, otherwise use getApiUrl
  const apiUrl = url.startsWith('http') ? url : url; // If not http, assume it's already a full relative path like /api/...
  const response = await fetch(apiUrl);
  if (!response.ok) {
    const error = new Error('Failed to fetch gallery data');
    // Attach extra info to the error object
    (error as any).status = response.status;
    throw error;
  }
  return response.json();
};

export default function GalleryPage() {
  const [selectedImage, setSelectedImage] = useState<any>(null);
  const [imageLoaded, setImageLoaded] = useState<Set<number>>(new Set());
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [categories, setCategories] = useState<string[]>([]);
  
  // Alias FaTimes as XMarkIcon for compatibility
  const XMarkIcon = FaTimes;
  
  // Fetch gallery data with category filter
  const { data, error, isLoading } = useSWR(
    getApiUrl(`gallery${selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''}`), 
    fetcher
  );
  
  // Fetch available categories
  useEffect(() => {
    fetch(getApiUrl('gallery/meta/categories'))
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setCategories(['all', ...data]);
        }
      })
      .catch(err => {
        console.error('Error loading gallery categories:', err);
      });
  }, []);
  
  const list = Array.isArray(data) ? data : [];
  const heroImage = 'https://i.pinimg.com/736x/42/59/80/42598064b25754c5bccb9bfd9e802193.jpg';
  const ambientOrbs: {
    className: string;
    size: string;
    style: CSSProperties;
    duration: number;
    delay: number;
  }[] = [
    {
      className: 'bg-soft-gold/35',
      size: 'w-56 h-56',
      style: { top: '10%', left: '12%' },
      duration: 18,
      delay: 0.4
    },
    {
      className: 'bg-rose-300/25',
      size: 'w-48 h-48',
      style: { bottom: '14%', right: '18%' },
      duration: 20,
      delay: 1.2
    },
    {
      className: 'bg-sky-300/20',
      size: 'w-64 h-64',
      style: { top: '-8%', right: '32%' },
      duration: 22,
      delay: 0.8
    }
  ];

  const handleImageLoad = (index: number) => {
    setImageLoaded(prev => {
      const newSet = new Set(prev);
      newSet.add(index);
      return newSet;
    });
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-cream via-sand/20 to-soft-gold/30">
      <section className="relative min-h-[78vh] pt-28 pb-20 flex items-center justify-center overflow-hidden">
        <Image
          src={heroImage}
          alt="Balinese cat lounge ambiance"
          fill
          priority
          className="object-cover"
          sizes="100vw"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/70" />
        <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/40 via-transparent to-transparent" />
        {ambientOrbs.map((orb, index) => (
          <motion.div
            key={index}
            className={`pointer-events-none absolute rounded-full blur-3xl ${orb.size} ${orb.className}`}
            style={orb.style}
            animate={{ opacity: [0.25, 0.6, 0.25], scale: [0.9, 1.08, 0.94] }}
            transition={{ duration: orb.duration, repeat: Infinity, ease: 'easeInOut', delay: orb.delay }}
          />
        ))}
        <motion.div
          className="relative z-10 max-w-4xl mx-auto text-center text-white px-6"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
        >
          <motion.span
            className="uppercase tracking-[0.4em] text-sm md:text-base text-soft-gold/80 block mb-6"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Signature Portraiture
          </motion.span>
          <motion.h1
            className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            LINDY Portrait Gallery
          </motion.h1>
          <motion.p
            className="text-lg md:text-xl text-white/85 mb-10 leading-relaxed"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Wander through luminous vignettes of our Balinese queens, stud princes, and kittens in residence — each frame captured within the serenity of the LINDY maison.
          </motion.p>
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <Link
              href="/waiting-list"
              className="px-8 py-3 bg-soft-gold text-white rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-soft-gold/90 transition-all"
            >
              Join the Collector List
            </Link>
            <Link
              href="/contact"
              className="px-8 py-3 border-2 border-white text-white rounded-xl font-semibold hover:bg-white hover:text-warm-brown transition-all"
            >
              Schedule a Salon Preview
            </Link>
          </motion.div>
        </motion.div>
      </section>

      <div className="relative">
        <div className="pointer-events-none absolute inset-x-0 -top-24 h-40 bg-gradient-to-b from-black/0 via-black/10 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 -mt-10">
        <div className="pointer-events-none absolute inset-x-6 -top-24 h-32 bg-gradient-to-b from-soft-gold/25 via-cream/10 to-transparent blur-3xl" />

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          {[
            { title: 'Signature Moments', detail: 'From sun-soaked window perches to velvet chaise lounges, every frame celebrates instinctive elegance.' },
            { title: 'Behind the Scenes', detail: 'Peek into nursery rituals, grooming spas, and sensory enrichment woven into our daily cadence.' },
            { title: 'Family Stories', detail: 'Discover updates from LINDY guardians as kittens blossom into lifelong confidants.' }
          ].map((item) => (
            <div key={item.title} className="bg-white/80 backdrop-blur rounded-3xl border border-sand/20 p-6 shadow-sm">
              <h3 className="font-heading text-lg text-warm-brown mb-2">{item.title}</h3>
              <p className="text-sm text-warm-brown/70 leading-relaxed">{item.detail}</p>
            </div>
          ))}
        </motion.div>

        {isLoading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(i => <SkeletonCard key={i} />)}
          </div>
        )}

        {error && !data && (
          <motion.p 
            className="text-center text-red-600 text-lg py-16"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            Failed to load gallery images.
          </motion.p>
        )}

        {(!isLoading && list.length === 0) && (
          <motion.div 
            className="text-center py-16"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <p className="text-xl text-warm-brown/60">Our latest portraits are being curated.</p>
            <p className="text-warm-brown/70 mt-2">Join our newsletter for first glimpses of upcoming LINDY moments.</p>
          </motion.div>
        )}

        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-8 justify-center">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-soft-gold text-white'
                  : 'bg-white/80 text-warm-brown hover:bg-white/90'
              }`}
            >
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </button>
          ))}
        </div>

        <motion.div 
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          key={selectedCategory}
        >
          {list.map((image: any, index: number) => (
            <motion.figure 
              key={image.id}
              className="group relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer bg-white/90 backdrop-blur"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
              onClick={() => setSelectedImage(image)}
            >
              <div className="relative w-full aspect-square overflow-hidden">
                {!imageLoaded.has(index) && (
                  <div className="absolute inset-0 bg-gray-200 animate-pulse rounded-3xl z-0" />
                )}
                <Image 
                  src={image.image_url || image.imageUrl || '/placeholder.jpg'}
                  alt={image.caption || image.title || 'Gallery image'} 
                  fill 
                  className="object-cover transition-all duration-500 group-hover:scale-110"
                  style={{
                    opacity: imageLoaded.has(index) ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                  sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                  onLoadingComplete={() => handleImageLoad(index)}
                  onError={(e) => {
                    console.error('Error loading image:', image.image_url || image.imageUrl, e);
                    // Fallback to a placeholder if image fails to load
                    const target = e.target as HTMLImageElement;
                    if (target.src !== '/placeholder.jpg') {
                      target.src = '/placeholder.jpg';
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                {image.caption && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                    <p className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      {image.caption}
                    </p>
                  </div>
                )}
              </div>
            </motion.figure>
          ))}
        </motion.div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <Lightbox image={selectedImage} onClose={() => setSelectedImage(null)} />
        )}
      </AnimatePresence>
    </main>
  )
}

function SkeletonCard(){
  return (
    <div className="bg-white/90 backdrop-blur rounded-3xl shadow-lg animate-pulse overflow-hidden">
      <div className="w-full aspect-square bg-gray-200"></div>
    </div>
  )
}

const Lightbox = ({ image, onClose }: { image: any; onClose: () => void }) => {
  if (!image) return null;
  
  // Ensure we have a valid image URL
  const imageUrl = image.image_url || image.thumbnail_url || '';
  
  // Handle potential Cloudinary URL transformation
  const optimizedImageUrl = imageUrl.includes('cloudinary.com') 
    ? `${imageUrl.split('upload/')[0]}upload/c_scale,w_1200/${imageUrl.split('upload/')[1]}`
    : imageUrl;

  return (
    <div 
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button 
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className="absolute top-6 right-6 text-white hover:text-soft-gold transition-colors z-10 p-2"
        aria-label="Close lightbox"
      >
        <FaTimes className="h-8 w-8" />
      </button>
      <div 
        className="relative w-full max-w-5xl max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative w-full h-full flex items-center justify-center">
          {imageUrl ? (
            <Image
              src={optimizedImageUrl}
              alt={image.title || 'Gallery image'}
              width={1200}
              height={800}
              className="w-auto h-auto max-w-full max-h-[80vh] object-contain"
              priority
              unoptimized={!optimizedImageUrl.includes('cloudinary.com')}
              onError={(e) => {
                // Fallback to original URL if optimized version fails
                if (e.currentTarget.src !== imageUrl) {
                  e.currentTarget.src = imageUrl;
                } else {
                  console.error('Failed to load image:', imageUrl);
                }
              }}
            />
          ) : (
            <div className="bg-gray-800/50 w-full h-64 flex items-center justify-center rounded-lg">
              <span className="text-white/60">Image not available</span>
            </div>
          )}
        </div>
        {(image.title || image.description) && (
          <div className="mt-4 text-center text-white bg-black/60 p-4 rounded-lg">
            {image.title && <h3 className="text-xl font-semibold">{image.title}</h3>}
            {image.description && <p className="mt-2 text-white/80">{image.description}</p>}
          </div>
        )}
      </div>
    </div>
  );
};
